import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, BarChart3, GitBranch, Table } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { FullScreenTakeover } from './FullScreenTakeover';
import { NodeDataTable } from './NodeDataTable';
import { ColumnLineageGraph, type ColumnLineageGraph as ColumnLineageGraphType } from './ColumnLineageGraph';
import { useColumnValueDistribution } from '../hooks/useColumnValueDistribution';
import ApiService from '../services/api.service';
import { toastService } from '../services/toast.service';
import { formatPercentage } from '../services/formatting.service';
import type { PipelineNode, PipelineNodeField } from '../services/pipelineNode.service';

interface ColumnStats {
  key: string;
  distinct_values: number;
  empty_values: number;
  uniqueness: number;
  density: number;
  samples: any[];
  enhanced?: boolean;
}

/**
 * What the quick nav currently has selected. Selecting doesn't navigate — `detailPath` is
 * where the "Go to Object" button in this panel goes.
 */
export type QuickNavSelection =
  | { kind: 'node'; node: PipelineNode; detailPath: string }
  | { kind: 'metric'; label: string; detailPath: string }
  | { kind: 'dimension'; label: string; detailPath: string };

/**
 * Bottom half of the quick nav: details of whatever is selected. Pipeline nodes (models and
 * entities alike) get their data, dbt ref and columns; metrics and dimensions are a
 * placeholder until there's something worth showing for them.
 */
export function QuickNavDetailPanel({ selection }: { selection: QuickNavSelection | null }) {
  const [showDataTable, setShowDataTable] = useState(false);

  // Column stats dialog
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [statsNodeId, setStatsNodeId] = useState<string>('');
  const [statsField, setStatsField] = useState<PipelineNodeField | null>(null);
  const [columnStats, setColumnStats] = useState<ColumnStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Column lineage takeover
  const [lineageOpen, setLineageOpen] = useState(false);
  const [lineageNodeId, setLineageNodeId] = useState<string>('');
  const [lineageField, setLineageField] = useState<PipelineNodeField | null>(null);
  const [columnLineage, setColumnLineage] = useState<ColumnLineageGraphType | null>(null);
  const [loadingLineage, setLoadingLineage] = useState(false);

  const columnDistributionQuery = useColumnValueDistribution(
    statsNodeId,
    statsField?.id,
    20,
    statsDialogOpen && !!statsNodeId && !!statsField?.id
  );

  const handleViewStats = useCallback(async (nodeId: string, field: PipelineNodeField) => {
    setStatsNodeId(nodeId);
    setStatsField(field);
    setStatsDialogOpen(true);
    setLoadingStats(true);
    setColumnStats(null);

    const fieldKey = field.id || field.name;
    if (!fieldKey) {
      toastService.error('Field ID or name is required');
      setLoadingStats(false);
      return;
    }

    try {
      const result = await ApiService.getInstance().request(
        'GET',
        `/pipelinenodes/${nodeId}/column/${fieldKey}/shape`
      ) as { record: ColumnStats };

      setColumnStats(result.record);
    } catch (err) {
      toastService.error('Failed to load column statistics');
      console.error('Error loading column stats:', err);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const handleViewLineage = useCallback(async (nodeId: string, field: PipelineNodeField) => {
    setLineageNodeId(nodeId);
    setLineageField(field);
    setLineageOpen(true);
    setLoadingLineage(true);
    setColumnLineage(null);

    if (!field.id) {
      toastService.error('Field ID is required');
      setLoadingLineage(false);
      return;
    }

    try {
      const result = await ApiService.getInstance().request(
        'GET',
        `/pipelinenodes/${nodeId}/column-lineage`,
        { field_id: field.id }
      ) as ColumnLineageGraphType;

      setColumnLineage(result);
    } catch (err) {
      toastService.error('Failed to load column lineage');
      console.error('Error loading column lineage:', err);
    } finally {
      setLoadingLineage(false);
    }
  }, []);

  const node = selection?.kind === 'node' ? selection.node : null;

  const renderBody = () => {
    if (!selection) {
      return (
        <div className="flex items-center justify-center h-full">
          <p className="text-xs text-muted-foreground">Select an object to view its details</p>
        </div>
      );
    }

    if (selection.kind !== 'node') {
      const label = selection.kind === 'metric' ? 'metric' : 'dimension';
      return (
        <div className="space-y-4">
          <h3 className="text-xs font-semibold text-foreground truncate" title={selection.label}>
            {selection.label}
          </h3>
          <Button variant="outline" size="sm" className="w-1/2 justify-start h-7 text-xs" asChild>
            <Link to={selection.detailPath}>
              <ExternalLink className="h-3.5 w-3.5 mr-2" />
              Go to Object
            </Link>
          </Button>
          <p className="text-xs text-muted-foreground">
            No preview available for a {label} yet.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="w-1/2 justify-start h-7 text-xs"
            onClick={() => setShowDataTable(true)}
          >
            <Table className="h-3.5 w-3.5 mr-2" />
            View Data
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="w-1/2 justify-start h-7 text-xs"
            asChild
          >
            <Link to={selection.detailPath}>
              <ExternalLink className="h-3.5 w-3.5 mr-2" />
              Go to Object
            </Link>
          </Button>
        </div>
        <div>
          <h3 className="text-xs font-semibold mb-2 text-foreground">DBT Ref</h3>
          <div className="flex items-center justify-between px-3 py-2 bg-muted rounded-md">
            <code className="text-xs font-mono">{node!.name || '-'}</code>
          </div>
        </div>
        <div>
          <h3 className="text-xs font-semibold mb-2 text-foreground">Columns</h3>
          {node!.fields && node!.fields.length > 0 ? (
            <div className="space-y-1">
              {node!.fields.map((field) => {
                const fieldName = field.name || field.label || '-';
                return (
                  <div
                    key={field.id || field.name}
                    className="flex items-center justify-between px-3 py-1.5 bg-muted rounded-md text-xs"
                  >
                    <span className="truncate flex-1 min-w-0">{fieldName}</span>
                    <div className="flex items-center gap-0.5 shrink-0 -mr-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5"
                        onClick={() => handleViewStats(node!.id, field as PipelineNodeField)}
                        title="View Stats"
                      >
                        <BarChart3 className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5"
                        onClick={() => handleViewLineage(node!.id, field as PipelineNodeField)}
                        title="View Lineage"
                      >
                        <GitBranch className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No fields available</p>
          )}
        </div>
      </div>
    );
  };

  const statsFieldName = statsField?.label || statsField?.name || 'Unknown Column';
  const lineageFieldName = lineageField?.label || lineageField?.name || 'Unknown Column';
  const nodeName = node?.label || node?.name || 'Unknown Node';

  return (
    <div className="h-[40%] overflow-auto p-4 border-t border-border">
      {renderBody()}

      {/* Data Table Takeover */}
      <FullScreenTakeover
        show={showDataTable}
        onHide={() => setShowDataTable(false)}
        title={`Data: ${nodeName}`}
      >
        {node && (
          <div className="p-6 h-full">
            <NodeDataTable pipelineNodeId={node.id} />
          </div>
        )}
      </FullScreenTakeover>

      {/* Column Stats Dialog */}
      <Dialog open={statsDialogOpen} onOpenChange={setStatsDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Column Statistics</DialogTitle>
            <DialogDescription>
              {statsField && `Statistics for "${statsFieldName}" from "${nodeName}"`}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4 py-4">
            {loadingStats ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-muted-foreground">Loading statistics...</div>
              </div>
            ) : columnStats ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Density</div>
                    <div className="text-2xl font-semibold">
                      {formatPercentage(columnStats.density)}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Density measures how many records have a value in this column.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Uniqueness</div>
                    <div className="text-2xl font-semibold">
                      {formatPercentage(columnStats.uniqueness)}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Uniqueness measures how many distinct values exist in this column.
                    </p>
                  </div>
                </div>

                {columnStats.distinct_values !== undefined && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Distinct Values</div>
                    <div className="text-lg">{columnStats.distinct_values.toLocaleString()}</div>
                  </div>
                )}

                {columnStats.empty_values !== undefined && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Empty Values</div>
                    <div className="text-lg">{columnStats.empty_values.toLocaleString()}</div>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="text-sm font-medium">Most Common Values</div>
                  {columnDistributionQuery.isLoading ? (
                    <div className="text-sm text-muted-foreground py-2">Loading distribution...</div>
                  ) : columnDistributionQuery.data && columnDistributionQuery.data.length > 0 ? (
                    <div className="space-y-2">
                      {columnDistributionQuery.data.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-2 bg-muted rounded"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-mono truncate" title={String(item.value)}>
                              {item.value === null || item.value === '' ? (
                                <span className="text-muted-foreground italic">(empty)</span>
                              ) : (
                                String(item.value)
                              )}
                            </div>
                          </div>
                          <div className="ml-4 text-sm font-semibold">
                            {item.total.toLocaleString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground py-2">
                      No distribution data available
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground py-4">
                No statistics available for this column.
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setStatsDialogOpen(false);
                setStatsNodeId('');
                setStatsField(null);
                setColumnStats(null);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Column Lineage Takeover */}
      <FullScreenTakeover
        show={lineageOpen}
        onHide={() => {
          setLineageOpen(false);
          setLineageNodeId('');
          setLineageField(null);
          setColumnLineage(null);
        }}
        noCloseButton={true}
        title={lineageField ? `Column Lineage: "${lineageFieldName}" from "${nodeName}"` : 'Column Lineage'}
      >
        <div className="h-full">
          {loadingLineage ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-sm text-muted-foreground">Loading lineage...</div>
            </div>
          ) : columnLineage ? (
            <div className="h-full w-full">
              <ColumnLineageGraph
                graph={columnLineage}
                selectedNodeId={lineageNodeId}
                selectedFieldId={lineageField?.id}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-sm text-muted-foreground">No lineage data available</div>
            </div>
          )}
        </div>
      </FullScreenTakeover>
    </div>
  );
}
